import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { useState, useContext } from "react";
import {
    InvalidReasons,
    NotificationContext,
    Button,
} from "@solsys/solsys-reactjs";
import SaveButton from "../elements/SaveButton";
import { useIdempotentCreate, fingerprintProperties, mutationInput } from "../../utils/idempotency";
import CraneDropDown from "../../dropdowns/CraneDropDown";
import CustomerDropDown from "../../dropdowns/CustomerDropDown";
import TicketDropDown from "../../dropdowns/TicketDropDown";
import ServiceDropDown from "../../dropdowns/ServiceDropDown";

const CLOSED_TICKET_FILTER = {
    field: "status_id",
    operator: "=",
    value: "2",
};

const fsrMutation = gql`
  mutation fsrCreate($fsr_mutation_request: MutationInput) {
    fsr(fsr_mutation_request: $fsr_mutation_request) {
      id
      isNew
    }
  }
`;

const ticketMutation = gql`
  mutation ticketFsrAssign($ticket_mutation_request: MutationInput) {
    ticket(ticket_mutation_request: $ticket_mutation_request) {
      id
    }
  }
`;

const serviceMutation = gql`
  mutation serviceFsrAssign($service_mutation_request: MutationInput) {
    service(service_mutation_request: $service_mutation_request) {
      id
    }
  }
`;

const previewTicketsQuery = gql`
  query fsrPreviewTickets($ticket_list_request: ticket_list_request) {
    ticket(ticket_list_request: $ticket_list_request) {
      edges {
        id
        name
      }
    }
  }
`;

const previewServicesQuery = gql`
  query fsrPreviewServices($service_list_request: service_list_request) {
    service(service_list_request: $service_list_request) {
      edges {
        id
        service_definition {
          name
        }
      }
    }
  }
`;

const STEPS = [
    { key: "crane", label: "1. Select crane" },
    { key: "tickets", label: "2. Select tickets" },
    { key: "services", label: "3. Select services" },
    { key: "preview", label: "4. Preview & save" },
];

export default function FSRCreate({ navigate }) {
    const { addNotification } = useContext(NotificationContext);
    const [step, setStep] = useState(0);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [selectedCraneId, setSelectedCraneId] = useState(null);
    const [selectedTicketIds, setSelectedTicketIds] = useState([]);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);
    const [invalidReasons, setInvalidReasons] = useState([]);
    const fsrFingerprint = fingerprintProperties([
        { key: "crane_id", number: selectedCraneId ? parseInt(selectedCraneId, 10) : null },
        { key: "customer_id", number: selectedCustomerId ? parseInt(selectedCustomerId, 10) : null },
        { key: "tickets", string: JSON.stringify(selectedTicketIds) },
        { key: "services", string: JSON.stringify(selectedServiceIds) },
    ]);
    const { idempotencyKey, isSaving, trackSave, resetAfterSuccess } = useIdempotentCreate({
        fingerprint: fsrFingerprint,
    });

    const [loadPreviewTickets, { data: previewTickets }] = useLazyQuery(previewTicketsQuery);
    const [loadPreviewServices, { data: previewServices }] = useLazyQuery(previewServicesQuery);

    const [createFsr] = useMutation(fsrMutation);
    const [assignTicket] = useMutation(ticketMutation);
    const [assignService] = useMutation(serviceMutation);

    const validateStep = (stepIndex) => {
        const reasons = [];
        if (stepIndex === 0) {
            if (!selectedCraneId) {
                reasons.push("Select a crane to continue.");
            }
        }
        if (stepIndex === 2) {
            const ticketCount = selectedTicketIds?.length ?? 0;
            const serviceCount = selectedServiceIds?.length ?? 0;
            if (ticketCount === 0 && serviceCount === 0) {
                reasons.push("Select at least one closed ticket or unassigned service.");
            }
        }
        setInvalidReasons(reasons);
        return reasons.length === 0;
    };

    const goNext = () => {
        if (!validateStep(step)) return;
        if (step === 2) {
            const ticketIn = selectedTicketIds.length
                ? `(${selectedTicketIds.join(", ")})`
                : null;
            const serviceIn = selectedServiceIds.length
                ? `(${selectedServiceIds.join(", ")})`
                : null;
            if (ticketIn) {
                loadPreviewTickets({
                    variables: {
                        ticket_list_request: {
                            filters: [{ field: "id", operator: "in", value: ticketIn }],
                            pagination: { limit: 500 },
                        },
                    },
                });
            }
            if (serviceIn) {
                loadPreviewServices({
                    variables: {
                        service_list_request: {
                            filters: [{ field: "id", operator: "in", value: serviceIn }],
                            pagination: { limit: 500 },
                        },
                    },
                });
            }
        }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };

    const goBack = () => {
        setInvalidReasons([]);
        setStep((s) => Math.max(s - 1, 0));
    };

    const handleSave = async () => {
        if (!validateStep(2)) {
            setStep(2);
            return;
        }

        await trackSave(async () => {
        try {
            const fsrProperties = [
                { key: "crane_id", number: parseInt(selectedCraneId, 10) },
            ];
            if (selectedCustomerId) {
                fsrProperties.push({
                    key: "customer_id",
                    number: parseInt(selectedCustomerId, 10),
                });
            }

            const fsrResponse = await createFsr({
                variables: {
                    fsr_mutation_request: mutationInput({
                        properties: fsrProperties,
                        idempotencyKey,
                    }),
                },
            });

            const savedFsrId = fsrResponse?.data?.fsr?.id;
            if (!savedFsrId) {
                throw new Error("Failed to create FSR");
            }

            for (const ticketId of selectedTicketIds) {
                await assignTicket({
                    variables: {
                        ticket_mutation_request: {
                            id: parseInt(ticketId, 10),
                            properties: [{ key: "fsr_id", number: parseInt(savedFsrId, 10) }],
                        },
                    },
                });
            }

            for (const serviceId of selectedServiceIds) {
                await assignService({
                    variables: {
                        service_mutation_request: {
                            id: parseInt(serviceId, 10),
                            properties: [{ key: "fsr_id", number: parseInt(savedFsrId, 10) }],
                        },
                    },
                });
            }

            addNotification("good", "FSR created successfully");
            resetAfterSuccess();
            navigate({
                action: "detail",
                id: savedFsrId,
                push: true,
            });
        } catch (error) {
            console.error(error);
            addNotification("bad", error.message || "Something went wrong");
        }
        });
    };

    const ticketPreview = previewTickets?.ticket?.edges ?? [];
    const servicePreview = previewServices?.service?.edges ?? [];

    return (
        <div className="fsr-create">
            <div className="page-header no-print">
                <span className="page-header-title">New FSR</span>
            </div>

            <nav className="fsr-step-nav no-print" aria-label="FSR creation steps">
                {STEPS.map((s, i) => (
                    <span
                        key={s.key}
                        className={i === step ? "fsr-step active" : "fsr-step"}
                    >
                        {s.label}
                    </span>
                ))}
            </nav>

            <div className="add-edit">
                {step === 0 && (
                    <div className="edit-section">
                        <p className="fsr-step-hint">
                            Customer is optional. Leave it blank to choose from cranes with no customer assigned, or select a customer to filter that customer's cranes.
                        </p>
                        <div className="edit-property">
                            <label>Customer (optional):</label>
                            <CustomerDropDown
                                selected={selectedCustomerId}
                                onSelect={(id) => {
                                    setSelectedCustomerId(id);
                                    setSelectedCraneId(null);
                                }}
                                searchEnabled
                            />
                        </div>
                        <div className="edit-property">
                            <label>Crane:</label>
                            <CraneDropDown
                                selected={selectedCraneId}
                                onSelect={setSelectedCraneId}
                                searchEnabled
                                multiple={false}
                                filters={
                                    selectedCustomerId
                                        ? [{
                                            field: "customer_id",
                                            operator: "=",
                                            value: String(selectedCustomerId),
                                        }]
                                        : [{
                                            field: "customer_id",
                                            operator: "is",
                                            value: "null",
                                        }]
                                }
                            />
                        </div>
                    </div>
                )}

                {step === 1 && (
                    <div className="edit-section">
                        <p className="fsr-step-hint">
                            Select closed tickets for this crane that are not already on an FSR.
                        </p>
                        <div className="edit-property">
                            <label>Tickets:</label>
                            <TicketDropDown
                                selected={selectedTicketIds}
                                onSelect={setSelectedTicketIds}
                                searchEnabled
                                multiple
                                craneId={selectedCraneId}
                                onlyWithoutFsr
                                filters={[CLOSED_TICKET_FILTER]}
                                disabled={!selectedCraneId}
                            />
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="edit-section">
                        <p className="fsr-step-hint">
                            Select services for this crane that are not already on an FSR.
                        </p>
                        <div className="edit-property">
                            <label>Services:</label>
                            <ServiceDropDown
                                selected={selectedServiceIds}
                                onSelect={setSelectedServiceIds}
                                searchEnabled
                                multiple
                                craneId={selectedCraneId}
                                onlyWithoutFsr
                                disabled={!selectedCraneId}
                            />
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="edit-section fsr-preview-summary">
                        <p className="fsr-step-hint">
                            Review selections, then save. FSRs cannot be edited after creation.
                        </p>
                        <p><strong>Crane ID:</strong> {selectedCraneId}</p>
                        <h4>Tickets ({selectedTicketIds.length})</h4>
                        {ticketPreview.length > 0 ? (
                            <ul>
                                {ticketPreview.map((t) => (
                                    <li key={t.id}>#{t.id} — {t.name}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-muted">None selected</p>
                        )}
                        <h4>Services ({selectedServiceIds.length})</h4>
                        {servicePreview.length > 0 ? (
                            <ul>
                                {servicePreview.map((s) => (
                                    <li key={s.id}>
                                        #{s.id} — {s.service_definition?.name || "Service"}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-muted">None selected</p>
                        )}
                    </div>
                )}

                {invalidReasons.length > 0 && (
                    <InvalidReasons reasons={invalidReasons} />
                )}

                <div className="edit-section fsr-step-actions no-print">
                    {step > 0 && (
                        <button type="button" onClick={goBack}>
                            Back
                        </button>
                    )}
                    {step < STEPS.length - 1 && (
                        <Button type="button" onClick={goNext}>
                            Next
                        </Button>
                    )}
                    {step === STEPS.length - 1 && (
                        <SaveButton type="button" onClick={handleSave} saving={isSaving}>
                            Save FSR
                        </SaveButton>
                    )}
                </div>
            </div>
        </div>
    );
}
